import { riverChainData, riverConfigFor } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { buildRiverLensCall, usesRiverLens } from './lens'

/**
 * User-data call build for River (Satoshi Protocol). Troves are keyed by
 * OWNER ADDRESS — one per user per TroveManager — so there is NO discovery
 * phase and the layout is fully deterministic from config:
 *
 * per market (config order):
 *   TroveManager.getEntireDebtAndColl(account)   (incl. pending redistribution)
 *   TroveManager.getTroveStatus(account)
 *   TroveManager.surplusBalances(account)        (post-liquidation claimable)
 * then once per chain:
 *   XApp.getCompoundedDebtDeposit(account)       (single StabilityPool)
 */
export const RIVER_CALLS_PER_MARKET = 3

export const buildRiverUserCall = (
  chainId: string,
  lender: string,
  account: string,
): Call[] => {
  const cfg = riverConfigFor(lender, chainId)
  const markets = riverChainData(lender, chainId)?.markets ?? []
  if (!cfg || markets.length === 0) return []

  // A chain without a lens deployment keeps the per-manager walk — the rollout
  // switch and the emergency revert both.
  if (usesRiverLens(chainId)) {
    const lensCall = buildRiverLensCall(chainId, cfg, markets, account)
    if (lensCall.length) return lensCall
  }

  const calls: Call[] = markets.flatMap((m) => [
    {
      address: m.troveManager,
      name: 'getEntireDebtAndColl',
      params: [account],
    },
    { address: m.troveManager, name: 'getTroveStatus', params: [account] },
    { address: m.troveManager, name: 'surplusBalances', params: [account] },
  ])
  calls.push({
    address: cfg.xapp,
    name: 'getCompoundedDebtDeposit',
    params: [account],
  })
  return calls
}
